/** Tipos e rótulos do Financeiro. Valores em reais chegam como string com duas casas ("333.33"). */

export type FinEntryType = "PAGAR" | "RECEBER";
export type FinEntryStatus = "PENDENTE" | "ATRASADO" | "PARCIAL" | "PAGO" | "CANCELADO";
export type FinCategoryKind = "RECEITA" | "DESPESA";
export type FinAccountType = "CORRENTE" | "POUPANCA" | "CAIXA" | "OUTRA";
export type FinPaymentMethod = "PIX" | "TED" | "BOLETO" | "DINHEIRO" | "CARTAO" | "DEBITO_AUTOMATICO" | "OUTRO";
export type FinFrequency = "SEMANAL" | "QUINZENAL" | "MENSAL" | "ANUAL";

export const ENTRY_TYPE_LABELS: Record<FinEntryType, string> = {
  PAGAR: "A pagar",
  RECEBER: "A receber",
};

/** Categoria exigida pelo tipo: pagar é despesa, receber é receita. */
export const KIND_FOR_TYPE: Record<FinEntryType, FinCategoryKind> = {
  PAGAR: "DESPESA",
  RECEBER: "RECEITA",
};

export const ENTRY_STATUS_LABELS: Record<FinEntryStatus, string> = {
  PENDENTE: "Pendente",
  ATRASADO: "Atrasado",
  PARCIAL: "Parcial",
  PAGO: "Pago",
  CANCELADO: "Cancelado",
};

/** Classes de `dpBadge` (Dp.css) por situação. */
/** Origem do título. As do DP (ADR 0029) são previsão: o valor muda só no DP. */
export type FinEntrySource = "MANUAL" | "RECORRENCIA" | "FOLHA" | "FERIAS" | "RESCISAO" | "GUIA_FGTS" | "GUIA_INSS" | "PROVISAO";

export const ENTRY_SOURCE_LABELS: Record<FinEntrySource, string> = {
  MANUAL: "Manual",
  RECORRENCIA: "Repetição",
  FOLHA: "Folha",
  FERIAS: "Férias",
  RESCISAO: "Rescisão",
  GUIA_FGTS: "Guia FGTS",
  GUIA_INSS: "Guia INSS",
  PROVISAO: "Provisão",
};

export const DP_SOURCES: FinEntrySource[] = ["FOLHA", "FERIAS", "RESCISAO", "GUIA_FGTS", "GUIA_INSS", "PROVISAO"];

/** Só competência (provisão do mês seguinte, ADR 0036): entra na DRE, fora do caixa e sem baixa. */
export const isCompetenceOnlySource = (source: FinEntrySource) => source === "PROVISAO";

export const isDpSource = (source: FinEntrySource) => DP_SOURCES.includes(source);

/** O que se sabe da origem no DP pela chave do título (para o link). */
export type FinEntryOrigin = {
  periodId: string | null;
  employeeId: string | null;
  part: "ADIANTAMENTO" | "LIQUIDO" | "CONSIGNADO" | "PENSAO" | "IRRF" | "MULTA_FGTS" | null;
};

export const ENTRY_STATUS_BADGE: Record<FinEntryStatus, string> = {
  PENDENTE: "dpBadge--orange",
  ATRASADO: "dpBadge--red",
  PARCIAL: "dpBadge--blue",
  PAGO: "dpBadge--green",
  CANCELADO: "dpBadge--gray",
};

/** No título a receber, "Pago" vira "Recebido". */
export const statusLabel = (status: FinEntryStatus, type: FinEntryType) =>
  status === "PAGO" && type === "RECEBER" ? "Recebido" : ENTRY_STATUS_LABELS[status];

export const ACCOUNT_TYPE_LABELS: Record<FinAccountType, string> = {
  CORRENTE: "Conta corrente",
  POUPANCA: "Poupança",
  CAIXA: "Caixa (dinheiro)",
  OUTRA: "Outra",
};

export const PAYMENT_METHOD_LABELS: Record<FinPaymentMethod, string> = {
  PIX: "PIX",
  TED: "TED / transferência",
  BOLETO: "Boleto",
  DINHEIRO: "Dinheiro",
  CARTAO: "Cartão",
  DEBITO_AUTOMATICO: "Débito automático",
  OUTRO: "Outro",
};

export const FREQUENCY_LABELS: Record<FinFrequency, string> = {
  SEMANAL: "Semanal",
  QUINZENAL: "Quinzenal",
  MENSAL: "Mensal",
  ANUAL: "Anual",
};

export type FinPaginated<T> = { page: number; limit: number; total: number; items: T[] };

export type FinAccount = {
  id: string;
  employerId: string;
  name: string;
  type: FinAccountType;
  bankCode: string | null;
  bankName: string | null;
  branch: string | null;
  accountNumber: string | null;
  initialBalance: string;
  initialBalanceAt: string;
  currentBalance: string;
  isActive: boolean;
};

export type FinCategory = {
  id: string;
  name: string;
  kind: FinCategoryKind;
  code: string | null;
  parentId: string | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  children: FinCategory[];
};

export type FinCounterparty = {
  id: string;
  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
  pixKey: string | null;
  notes: string | null;
  isActive: boolean;
};

export type FinCostCenterGroupRef = { id: string; code: string; name: string; parentId: string | null };

export type FinCostCenter = {
  id: string;
  code: string;
  name: string;
  storeIds: string[];
  /** Grupo ou subgrupo (DRE); null = sem grupo. */
  group: FinCostCenterGroupRef | null;
};

/** Grupo de centro de custo em árvore (até dois níveis), com os centros de cada nó. */
export type FinCostCenterGroup = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  costCenters: Array<{ id: string; code: string; name: string }>;
  children: FinCostCenterGroup[];
};

/** Fatia do rateio de um título. */
export type FinAllocation = { costCenter: { id: string; code: string; name: string }; amount: string };

export type FinStore = { id: string; name: string };

export type FinEntry = {
  id: string;
  employerId: string;
  type: FinEntryType;
  description: string;
  notes: string | null;
  category: { id: string; name: string; kind: FinCategoryKind; parentId: string | null } | null;
  costCenter: {
    id: string;
    code: string;
    name: string;
    group?: { id: string; code: string; name: string; parent: { id: string; code: string; name: string } | null } | null;
  } | null;
  /** Rateio entre centros; vazio = 100% no costCenter. */
  allocations: FinAllocation[];
  store: { id: string; nomeFantasia: string } | null;
  counterparty: { id: string; name: string; document: string | null } | null;
  suggestedAccountId: string | null;
  competenceDate: string;
  dueDate: string;
  amount: string;
  settledAmount: string;
  openAmount: string;
  status: FinEntryStatus;
  isOverdue: boolean;
  lastPaidAt: string | null;
  documentGroupId: string | null;
  installmentNumber: number | null;
  installmentCount: number | null;
  recurrenceId: string | null;
  source: FinEntrySource;
  sourceRef: string | null;
  origin: FinEntryOrigin | null;
  canceledAt: string | null;
  cancelReason: string | null;
};

export type FinSettlement = {
  id: string;
  account: { id: string; name: string };
  paidAt: string;
  amount: string;
  method: FinPaymentMethod;
  notes: string | null;
  hasFile: boolean;
  fileName: string | null;
  createdBy: { id: string; nomeCompleto: string };
  createdAt: string;
};

export type FinEntryDetail = FinEntry & {
  settlements: FinSettlement[];
  installments: Array<{
    id: string;
    installmentNumber: number | null;
    dueDate: string;
    amount: string;
    openAmount: string;
    status: FinEntryStatus;
  }>;
};

export type FinEntryList = FinPaginated<FinEntry> & {
  totals: { count: number; amount: string; settled: string; open: string };
};

export type FinRecurrence = {
  id: string;
  type: FinEntryType;
  frequency: FinFrequency;
  startDate: string;
  endDate: string | null;
  amount: string;
  description: string;
  category: { id: string; name: string } | null;
  counterparty: { id: string; name: string } | null;
  costCenterId: string | null;
  allocations: Array<{ costCenterId: string; amount: string }>;
  accountId: string | null;
  generatedUntil: string;
  isActive: boolean;
};

export type FinCashflowBucket = {
  period: string;
  realizedIn: string;
  realizedOut: string;
  forecastIn: string;
  forecastOut: string;
  realizedBalance: string;
  balance: string;
};

export type FinCashflow = {
  from: string;
  to: string;
  granularity: "day" | "month";
  /** Com recorte de títulos não há saldo de conta: saldo inicial zero, saldo vira resultado. */
  scoped?: boolean;
  today: string;
  openingBalance: string;
  closingBalance: string;
  totals: { realizedIn: string; realizedOut: string; forecastIn: string; forecastOut: string };
  overdue: { in: string; out: string };
  buckets: FinCashflowBucket[];
};

export type FinDueSlot = { count: number; amount: string };
export type FinDueGroup = { overdue: FinDueSlot; today: FinDueSlot; next7: FinDueSlot; next30: FinDueSlot };

export type FinSummary = {
  today: string;
  totalBalance: string;
  accounts: Array<{ id: string; name: string; type: FinAccountType; currentBalance: string }>;
  payables: FinDueGroup;
  receivables: FinDueGroup;
  /** Títulos sem centro de custo e sem rateio, competência nos últimos 12 meses. */
  unclassified?: { count: number; amount: string };
};

// ── DRE por centro de custo ──────────────────────────────────────────────────

export type FinDreBasis = "competence" | "cash";

export type FinDreColumn = {
  key: string;
  kind: "costCenter" | "subgroupTotal" | "groupTotal" | "noCostCenter" | "total";
  id: string | null;
  label: string;
  code: string | null;
  group: { id: string | null; label: string } | null;
  subgroup: { id: string; label: string } | null;
};

export type FinDreRow = {
  key: string;
  kind: "kindTotal" | "category" | "subcategory" | "result";
  id: string | null;
  label: string;
  code: string | null;
  level: number;
  categoryKind: FinCategoryKind | null;
  cells: Record<string, string>;
};

export type FinDre = {
  basis: FinDreBasis;
  from: string;
  to: string;
  months: number;
  count: number;
  columns: FinDreColumn[];
  rows: FinDreRow[];
  summary: { revenue: string; expense: string; result: string; unclassified: string };
};

// ── Painel de contas a pagar e a receber (tela Fluxo de caixa) ───────────────

export type FinDashAcc = { count: number; amount: string; open: string };
export type FinDashTotals = FinDashAcc & { settled: string; overdue: string };
export type FinDashDimRow = { id: string | null; name: string; payable: FinDashAcc; receivable: FinDashAcc };
export type FinDashRankRow = FinDashAcc & { id: string | null; name: string; parentName?: string | null; others: boolean };
export type FinDashItem = {
  id: string;
  type: FinEntryType;
  description: string;
  counterparty: string | null;
  dueDate: string;
  open: string;
};

export type FinDashboard = {
  from: string;
  to: string;
  today: string;
  granularity: "day" | "month";
  basis: "due" | "competence";
  totals: { payable: FinDashTotals; receivable: FinDashTotals };
  realized: { in: string; out: string };
  overdue: { payable: { count: number; amount: string }; receivable: { count: number; amount: string } };
  series: Array<{ period: string; payable: string; payableOpen: string; receivable: string; receivableOpen: string }>;
  aging: Array<{
    key: string;
    label: string;
    payable: { count: number; amount: string };
    receivable: { count: number; amount: string };
  }>;
  byCostCenter: FinDashDimRow[];
  byStore: FinDashDimRow[];
  byCategory: { payable: FinDashRankRow[]; receivable: FinDashRankRow[] };
  byCounterparty: { payable: FinDashRankRow[]; receivable: FinDashRankRow[] };
  upcoming: FinDashItem[];
  biggestOverdue: FinDashItem[];
};
